import { useEffect, useState } from 'react';
import { api, ErrorApi, type JugadorPublico } from '../services/api';
import { TecladoPin } from '../components/TecladoPin';
import { Aviso, Boton, Cargando } from '../components/ui';
import { Marca } from '../components/Marca';
import { LEMA_APP } from '../marca';

/**
 * Entrar: elegis tu nombre de la lista y ponés tu PIN.
 *
 * Sin mails ni contrasenas. Como la sesion dura 90 dias, esta pantalla se ve
 * una vez por temporada, no todos los viernes.
 */
export function Entrar({ onEntro }: { onEntro: () => void }) {
  const [jugadores, setJugadores] = useState<JugadorPublico[] | null>(null);
  const [elegido, setElegido] = useState<JugadorPublico | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [intento, setIntento] = useState(0);

  useEffect(() => {
    api
      .paraEntrar()
      .then((r) => setJugadores(r.jugadores))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Error'));
  }, []);

  async function probar(pin: string) {
    if (!elegido) return;
    setOcupado(true);
    setError(null);
    try {
      await api.entrar(elegido.id, pin);
      onEntro();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo entrar.');
      setIntento((n) => n + 1); // borra lo tipeado
      setOcupado(false);
    }
  }

  if (error && !jugadores) {
    return (
      <Pantalla>
        <Aviso tono="error">{error}</Aviso>
      </Pantalla>
    );
  }

  if (!jugadores) {
    return (
      <Pantalla>
        <Cargando />
      </Pantalla>
    );
  }

  if (jugadores.length === 0) {
    return (
      <Pantalla>
        <Aviso tono="alerta">
          Todavia no hay jugadores cargados. Hay que crear el primer administrador — esta
          explicado en <strong>COMO USAR.md</strong>, paso 6.
        </Aviso>
      </Pantalla>
    );
  }

  if (!elegido) {
    return (
      <Pantalla>
        <p className="mb-4 text-sm text-(--color-apagado)">¿Quién sos?</p>
        <ul className="flex flex-col gap-2">
          {jugadores.map((jugador) => (
            <li key={jugador.id}>
              <button
                onClick={() => setElegido(jugador)}
                className="flex min-h-14 w-full items-center gap-3 rounded-xl border
                  border-(--color-borde) bg-(--color-panel) px-4 text-left transition
                  active:scale-[0.99]"
              >
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full
                    bg-(--color-acento) font-bold text-(--color-sobre-acento)"
                >
                  {jugador.nombre[0]?.toUpperCase()}
                </span>
                <span className="flex-1">{jugador.nombre}</span>
                {jugador.pin_provisorio === 1 && (
                  <span className="text-xs text-(--color-alerta)">PIN nuevo</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      </Pantalla>
    );
  }

  return (
    <Pantalla>
      <div className="mb-6 flex items-center justify-between">
        <p className="text-lg font-semibold">{elegido.nombre}</p>
        <Boton
          tipo="secundario"
          onClick={() => {
            setElegido(null);
            setError(null);
          }}
        >
          Cambiar
        </Boton>
      </div>

      <TecladoPin onCompleto={probar} ocupado={ocupado} reiniciar={intento} />

      {error && (
        <div className="mt-6">
          <Aviso tono="error">{error}</Aviso>
        </div>
      )}
    </Pantalla>
  );
}

function Pantalla({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto flex min-h-full max-w-md flex-col justify-center p-6">
      <header className="mb-8">
        <h1><Marca grande /></h1>
        <p className="mt-2 text-sm text-(--color-apagado)">{LEMA_APP}</p>
      </header>
      {children}
    </main>
  );
}
